import React, { Component } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import axios from "axios";
import { apiHost } from "../../api/apiClient";
import RoleAccessBadge from "../common/roleAccessBadge";

const DAYS = [
  { label: "Mon", bit: 2 },
  { label: "Tue", bit: 4 },
  { label: "Wed", bit: 8 },
  { label: "Thu", bit: 16 },
  { label: "Fri", bit: 32 },
  { label: "Sat", bit: 64 },
  { label: "Sun", bit: 1 },
];

const formatUserLocalTime = (utcValue) => {
  if (!utcValue) return "";
  const value = String(utcValue);
  const hasExplicitZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  const date = new Date(hasExplicitZone ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  }).format(date);
};

const emptyEditor = (depotId, timeZoneId) => ({
  id: null,
  depotId,
  name: "",
  assetIds: [],
  recipientUserIds: [],
  externalEmailAddresses: [],
  notifyOnEntry: true,
  notifyOnExit: true,
  emailEnabled: false,
  enabled: true,
  allDay: true,
  activeDaysMask: 127,
  startTime: "08:00",
  endTime: "18:00",
  timeZoneId: timeZoneId || "",
  confirmationSeconds: 0,
});

class DepotLiveAlertSubscriptions extends Component {
  state = {
    subscriptions: [],
    recentEvents: [],
    diagnostics: [],
    options: { assets: [], users: [] },
    editor: emptyEditor(this.props.depotId, this.props.timeZoneId),
    loading: true,
    saving: false,
    message: "",
    error: "",
    editorOpen: false,
    simulationSubscriptionId: "",
    simulationAssetId: "",
    simulationRunning: false,
    simulationDetailsOpen: true,
    pickerSearches: { assetIds: "", recipientUserIds: "" },
    externalEmailInput: "",
    issueSubscriptionId: "",
    issueStatusSelections: {},
    resolvingIssue: false,
  };

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.depotId !== this.props.depotId && this.props.depotId) {
      this.setState(
        { editor: emptyEditor(this.props.depotId, this.props.timeZoneId) },
        this.load
      );
    }
  }

  auth = () => ({ Authorization: "Bearer " + localStorage.getItem("token") });

  load = async () => {
    if (!this.props.depotId) return;
    this.setState({ loading: true, error: "" });
    try {
      const requests = [
        axios.get(`${apiHost}/api/DepotLiveAlerts/Depot/${this.props.depotId}`, { headers: this.auth() }),
        axios.get(`${apiHost}/api/DepotLiveAlerts/Options/${this.props.depotId}`, { headers: this.auth() }),
      ];
      if (this.props.systemAdmin) {
        requests.push(
          axios.get(`${apiHost}/api/DepotLiveAlerts/RecentEvents/${this.props.depotId}`, { headers: this.auth() }),
          axios.get(`${apiHost}/api/DepotLiveAlerts/Diagnostics/${this.props.depotId}`, { headers: this.auth() })
        );
      }
      const [subscriptions, options, recentEvents, diagnostics] = await Promise.all(requests);
      this.setState({
        subscriptions: subscriptions.data || [],
        options: options.data || { assets: [], users: [] },
        recentEvents: recentEvents ? recentEvents.data || [] : [],
        diagnostics: diagnostics ? diagnostics.data || [] : [],
        loading: false,
      });
    } catch (error) {
      this.setState({ loading: false, error: "Unable to load depot live-alert subscriptions." });
    }
  };

  startNew = () => {
    this.setState({
      editor: emptyEditor(this.props.depotId, this.state.options.timeZoneId || this.props.timeZoneId),
      editorOpen: true,
      pickerSearches: { assetIds: "", recipientUserIds: "" },
      externalEmailInput: "",
      message: "",
      error: "",
    });
  };

  edit = (subscription) => {
    this.setState({
      editor: {
        ...emptyEditor(this.props.depotId, this.state.options.timeZoneId || this.props.timeZoneId),
        ...subscription,
        assetIds: (subscription.assetIds || []).map(String),
        recipientUserIds: (subscription.recipientUserIds || []).map(String),
        externalEmailAddresses: subscription.externalEmailAddresses || [],
      },
      editorOpen: true,
      pickerSearches: { assetIds: "", recipientUserIds: "" },
      externalEmailInput: "",
      message: "",
      error: "",
    });
  };

  view = (subscription) => {
    this.edit(subscription);
  };

  setEditor = (changes) => {
    this.setState((state) => ({ editor: { ...state.editor, ...changes }, message: "", error: "" }));
  };

  toggleList = (field, id) => {
    const text = String(id);
    const current = this.state.editor[field] || [];
    this.setEditor({
      [field]: current.includes(text) ? current.filter((value) => value !== text) : [...current, text],
    });
  };

  toggleDay = (bit) => {
    this.setEditor({ activeDaysMask: this.state.editor.activeDaysMask ^ bit });
  };

  save = async () => {
    const editor = this.state.editor;
    if (!editor.name.trim()) return this.setState({ error: "Enter a subscription name." });
    if (!editor.assetIds.length) return this.setState({ error: "Select at least one asset." });
    if (!editor.recipientUserIds.length && !(editor.emailEnabled && editor.externalEmailAddresses.length)) {
      return this.setState({ error: "Select a notification recipient or add an external email address." });
    }
    if (!editor.notifyOnEntry && !editor.notifyOnExit) return this.setState({ error: "Select entry, exit, or both." });
    if (!editor.activeDaysMask) return this.setState({ error: "Select at least one active day." });

    this.setState({ saving: true, error: "", message: "" });
    try {
      await axios.post(`${apiHost}/api/DepotLiveAlerts`, editor, { headers: this.auth() });
      this.setState({
        saving: false,
        editorOpen: false,
        message: "Depot live-alert subscription saved.",
        editor: emptyEditor(this.props.depotId, this.state.options.timeZoneId || this.props.timeZoneId),
      });
      await this.load();
    } catch (error) {
      const detail = error.response && error.response.data;
      this.setState({
        saving: false,
        error: typeof detail === "string" ? detail : "Unable to save depot live-alert subscription.",
      });
    }
  };

  remove = async (subscription) => {
    if (!window.confirm(`Delete "${subscription.name}"?`)) return;
    try {
      await axios.delete(`${apiHost}/api/DepotLiveAlerts/${subscription.id}`, { headers: this.auth() });
      this.setState({ message: "Depot live-alert subscription deleted.", error: "" });
      await this.load();
    } catch (error) {
      this.setState({ error: "Unable to delete depot live-alert subscription." });
    }
  };

  openSimulation = (subscription) => {
    const resolvedAssetIds = (subscription.resolvedAssetIds || subscription.assetIds || []).map(String);
    this.setState({
      simulationSubscriptionId: String(subscription.id),
      simulationAssetId: resolvedAssetIds[0] || "",
      simulationDetailsOpen: true,
      message: "",
      error: "",
    });
  };

  toggleSubscription = async (subscription) => {
    this.setState({ saving: true, message: "", error: "" });
    try {
      await axios.post(
        `${apiHost}/api/DepotLiveAlerts/${subscription.id}/Enabled`,
        { enabled: !subscription.enabled },
        { headers: this.auth() }
      );
      this.setState({
        saving: false,
        message: `Subscription ${subscription.enabled ? "disabled" : "enabled"}.`,
      });
      await this.load();
    } catch (error) {
      const detail = error.response && error.response.data;
      this.setState({
        saving: false,
        error: typeof detail === "string" ? detail : "Unable to update the subscription.",
      });
    }
  };

  simulate = async (transitionType) => {
    if (!this.state.simulationAssetId) {
      this.setState({ error: "Select an asset to simulate." });
      return;
    }
    this.setState({ simulationRunning: true, message: "", error: "" });
    try {
      const response = await axios.post(
        `${apiHost}/api/DepotLiveAlerts/Simulate`,
        {
          subscriptionId: this.state.simulationSubscriptionId,
          assetId: this.state.simulationAssetId,
          transitionType,
        },
        { headers: this.auth() }
      );
      this.setState({
        simulationRunning: false,
        message: response.data.message || `Simulated ${transitionType.toLowerCase()} alert sent.`,
      });
      await this.load();
    } catch (error) {
      const detail = error.response && error.response.data;
      this.setState({
        simulationRunning: false,
        error: typeof detail === "string" ? detail : "Unable to run depot alert simulation.",
      });
    }
  };

  addExternalEmail = () => {
    const email = this.state.externalEmailInput.trim();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.setState({ error: "Enter a valid email address." });
      return;
    }
    const current = this.state.editor.externalEmailAddresses || [];
    if (current.some((item) => item.toLowerCase() === email.toLowerCase())) {
      this.setState({ externalEmailInput: "", error: "" });
      return;
    }
    this.setEditor({ externalEmailAddresses: [...current, email] });
    this.setState({ externalEmailInput: "" });
  };

  removeExternalEmail = (email) => {
    this.setEditor({
      externalEmailAddresses: (this.state.editor.externalEmailAddresses || []).filter((item) => item !== email),
    });
  };

  openIssues = (subscription) => {
    const eligibleStatuses = this.state.options.eligibleStatuses || [];
    const defaultStatusId = eligibleStatuses.length ? eligibleStatuses[0].id : "";
    const selections = {};
    (subscription.suspendedAssets || []).forEach((asset) => {
      selections[String(asset.id)] = defaultStatusId;
    });
    this.setState({
      issueSubscriptionId: String(subscription.id),
      issueStatusSelections: selections,
      error: "",
    });
  };

  resolveIssue = async (asset, action) => {
    const subscriptionId = this.state.issueSubscriptionId;
    this.setState({ resolvingIssue: true, error: "" });
    try {
      const body = action === "Remedy"
        ? { statusId: this.state.issueStatusSelections[String(asset.id)] || null }
        : {};
      await axios.post(
        `${apiHost}/api/DepotLiveAlerts/${subscriptionId}/SuspendedAsset/${asset.id}/${action}`,
        body,
        { headers: this.auth() }
      );
      await this.load();
      this.setState({
        resolvingIssue: false,
        issueSubscriptionId: "",
        message: action === "Confirm"
          ? "Confirmed. All ineligible depot notification associations for this asset were removed."
          : "Asset allocation remedied and depot notification restored.",
      });
    } catch (error) {
      const detail = error.response && error.response.data;
      this.setState({
        resolvingIssue: false,
        error: typeof detail === "string" ? detail : "Unable to resolve the suspended asset.",
      });
    }
  };

  renderChecklist = (title, field, items, emptyText, searchPlaceholder) => {
    const search = (this.state.pickerSearches[field] || "").trim().toLowerCase();
    const filteredItems = search
      ? items.filter((item) =>
          [item.name, item.email].some((value) => String(value || "").toLowerCase().includes(search))
        )
      : items;
    return (
      <div className="depot-live-alert-picker">
      <div className="depot-live-alert-picker-title">
        <strong>{title}</strong>
        <span>{(this.state.editor[field] || []).length} selected</span>
      </div>
      <div className="depot-live-alert-picker-search">
        <i className="fa fa-search" />
        <input
          type="search"
          value={this.state.pickerSearches[field] || ""}
          placeholder={searchPlaceholder}
          onChange={(event) => this.setState((state) => ({
            pickerSearches: { ...state.pickerSearches, [field]: event.target.value },
          }))}
        />
      </div>
      <div className="depot-live-alert-picker-list">
        {!items.length && <p>{emptyText}</p>}
        {!!items.length && !filteredItems.length && <p>No matching results.</p>}
        {filteredItems.map((item) => {
          const id = String(item.id);
          return (
            <label key={id} className={this.state.editor[field].includes(id) ? "selected" : ""}>
              <input
                type="checkbox"
                checked={this.state.editor[field].includes(id)}
                disabled={this.props.readOnly}
                onChange={() => this.toggleList(field, id)}
              />
              <span>
                <strong>{item.name || item.email}</strong>
                {item.email && item.name !== item.email && <small>{item.email}</small>}
              </span>
            </label>
          );
        })}
      </div>
    </div>
    );
  };

  render() {
    const readOnly = !!this.props.readOnly;
    const { subscriptions, recentEvents, diagnostics, options, editor, loading, saving, editorOpen, message, error } = this.state;
    const simulationSubscription = subscriptions.find(
      (item) => String(item.id) === this.state.simulationSubscriptionId
    );
    const issueSubscription = subscriptions.find(
      (item) => String(item.id) === this.state.issueSubscriptionId
    );
    const simulationAssetIds = simulationSubscription
      ? (simulationSubscription.resolvedAssetIds || simulationSubscription.assetIds || []).map(String)
      : [];
    const simulationAssets = (options.assets || []).filter((asset) =>
      simulationAssetIds.includes(String(asset.id))
    );
    const qualifyingAssetIds = new Set((options.assets || []).map((asset) => String(asset.id)));
    const suspendedSelectedAssetIds = (editor.assetIds || []).filter((id) => !qualifyingAssetIds.has(String(id)));
    const suspendedAssets = (editor.suspendedAssets || []).length
      ? editor.suspendedAssets
      : suspendedSelectedAssetIds.map((id) => ({ id, name: String(id), reason: "Not currently eligible" }));
    return (
      <section className="depot-edit-section depot-live-alert-section">
        <div className="depot-edit-section-title depot-live-alert-header">
          <div>
            <h3>Live Depot Alerts</h3>
            <p>Notify selected users when subscribed assets enter or leave this depot.</p>
          </div>
          <RoleAccessBadge role={this.props.systemAdmin ? "SystemAdmin" : "CommunityManager"} />
          {!readOnly && <Button type="button" variant="primary" onClick={this.startNew}>Add Subscription</Button>}
        </div>

        <div className="depot-live-alert-performance-note">
          {this.props.systemAdmin
            ? "Only subscribed assets are checked. The alert engine uses cached subscriptions and runs from the existing real-time update job."
            : "These are shared organisation settings. Changes made here are visible to all Community Managers in the organisation."}
        </div>

        {loading ? (
          <div className="depot-live-alert-empty">Loading subscriptions...</div>
        ) : subscriptions.length === 0 ? (
          <div className="depot-live-alert-empty">No live-alert subscriptions have been configured for this depot.</div>
        ) : (
          <div className="depot-live-alert-list">
            {subscriptions.map((subscription) => (
              <div className="depot-live-alert-row" key={subscription.id}>
                <div>
                  <strong>{subscription.name}</strong>
                  <span>
                    {subscription.notifyOnEntry && subscription.notifyOnExit ? "Entry and exit" : subscription.notifyOnEntry ? "Entry" : "Exit"}
                    {" · "}
                    {subscription.allDay ? "All day" : `${subscription.startTime}–${subscription.endTime}`}
                  </span>
                {!!(subscription.suspendedAssets || []).length && (
                    <button
                      type="button"
                      className="depot-live-alert-row-suspended"
                    onClick={() => this.openIssues(subscription)}
                    >
                      <i className="fa fa-pause-circle" />
                      Review {subscription.suspendedAssets.length} asset issue{subscription.suspendedAssets.length === 1 ? "" : "s"}
                    </button>
                  )}
                </div>
                <label className="depot-live-alert-switch depot-live-alert-row-switch">
                  <input
                    type="checkbox"
                    checked={subscription.enabled}
                    disabled={saving || readOnly}
                    onChange={() => this.toggleSubscription(subscription)}
                  />
                  <span />
                  <strong>{subscription.enabled ? "Enabled" : "Disabled"}</strong>
                </label>
                {readOnly ? (
                  <Button type="button" variant="outline-secondary" onClick={() => this.view(subscription)}>View</Button>
                ) : (
                  <>
                    <Button type="button" variant="outline-secondary" onClick={() => this.edit(subscription)}>Edit</Button>
                    {this.props.systemAdmin && <Button type="button" variant="outline-primary" onClick={() => this.openSimulation(subscription)}>Simulate</Button>}
                    <Button type="button" variant="outline-danger" onClick={() => this.remove(subscription)}>Delete</Button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {!readOnly && simulationSubscription && (
          <div className="depot-live-alert-simulator">
            <div className="depot-live-alert-simulator-title">
              <div>
                <span>System Admin Simulation</span>
                <h4>{simulationSubscription.name}</h4>
                <p>Runs the real transition, persistence, recipient and SignalR path without changing the asset’s genuine GPS position.</p>
              </div>
              <button type="button" onClick={() => this.setState({ simulationSubscriptionId: "", simulationAssetId: "" })}>×</button>
            </div>
            <div className="depot-live-alert-simulator-controls">
              <Form.Group>
                <Form.Label>Test asset</Form.Label>
                <Form.Control
                  as="select"
                  value={this.state.simulationAssetId}
                  onChange={(e) => this.setState({ simulationAssetId: e.target.value })}
                >
                  {!simulationAssets.length && <option value="">No assets resolved for this subscription</option>}
                  {simulationAssets.map((asset) => (
                    <option key={asset.id} value={asset.id}>{asset.name}</option>
                  ))}
                </Form.Control>
              </Form.Group>
              <Button
                type="button"
                variant="success"
                disabled={this.state.simulationRunning || !simulationSubscription.notifyOnEntry}
                onClick={() => this.simulate("Entry")}
              >
                Simulate Entry
              </Button>
              <Button
                type="button"
                variant="warning"
                disabled={this.state.simulationRunning || !simulationSubscription.notifyOnExit}
                onClick={() => this.simulate("Exit")}
              >
                Simulate Exit
              </Button>
            </div>
            <div className="depot-live-alert-simulation-warning">
              Test notifications and event-history records are labelled SIMULATED.
            </div>
          </div>
        )}

        {!readOnly && simulationSubscription && (
          <div className="depot-live-alert-test-details">
            <button
              type="button"
              className="depot-live-alert-test-details-toggle"
              onClick={() => this.setState((state) => ({ simulationDetailsOpen: !state.simulationDetailsOpen }))}
              aria-expanded={this.state.simulationDetailsOpen}
            >
              <span>
                <strong>Simulation Diagnostics</strong>
                <small>Recent crossings and live engine evaluation</small>
              </span>
              <i className={`fa fa-chevron-${this.state.simulationDetailsOpen ? "up" : "down"}`} />
            </button>
            {this.state.simulationDetailsOpen && (
              <div className="depot-live-alert-test-details-body">
        <div className="depot-live-alert-history">
          <div className="depot-live-alert-history-title">
            <div>
              <h4>Recent Detected Crossings</h4>
              <p>Persisted entry and exit transitions for this depot. This confirms geofence detection independently of live notification delivery.</p>
            </div>
            <Button type="button" variant="outline-secondary" onClick={this.load}>Refresh</Button>
          </div>
          {recentEvents.length === 0 ? (
            <div className="depot-live-alert-empty">No depot geofence crossings have been detected yet.</div>
          ) : (
            <div className="depot-live-alert-history-list">
              {recentEvents.map((event) => (
                <div key={event.id}>
                  <span className={String(event.transitionType).toLowerCase()}>
                    {event.isSimulated ? `Test ${event.transitionType}` : event.transitionType}
                  </span>
                  <strong>{event.assetName}</strong>
                  <time>{formatUserLocalTime(event.occurredUtc)}</time>
                  <small>{Number(event.latitude).toFixed(5)}, {Number(event.longitude).toFixed(5)}</small>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="depot-live-alert-history depot-live-alert-diagnostics">
          <div className="depot-live-alert-history-title">
            <div>
              <h4>Engine Diagnostics</h4>
              <p>Shows the last runtime evaluation for every cached subscription and asset.</p>
            </div>
            <Button type="button" variant="outline-secondary" onClick={this.load}>Refresh</Button>
          </div>
          {diagnostics.length === 0 ? (
            <div className="depot-live-alert-empty">
              No runtime diagnostics are available. The backend may not have loaded the subscription cache or received a changed GPS message since restart.
            </div>
          ) : (
            <div className="depot-live-alert-diagnostic-list">
              {diagnostics.map((item) => (
                <div key={`${item.subscriptionId}-${item.assetId}`}>
                  <div>
                    <strong>{item.assetName}</strong>
                    <small>{item.subscriptionName}</small>
                  </div>
                  <span className={item.inside === true ? "inside" : item.inside === false ? "outside" : ""}>
                    {item.inside === true ? "Inside" : item.inside === false ? "Outside" : "Not evaluated"}
                  </span>
                  <div>
                    <strong>{item.status}</strong>
                    <small>
                      {item.messageReceivedUtc ? `GPS ${formatUserLocalTime(item.messageReceivedUtc)}` : "Waiting for GPS"}
                    </small>
                  </div>
                  <small>
                    {item.latitude == null || item.longitude == null
                      ? "No coordinates"
                      : `${Number(item.latitude).toFixed(5)}, ${Number(item.longitude).toFixed(5)}`}
                  </small>
                </div>
              ))}
            </div>
          )}
        </div>
              </div>
            )}
          </div>
        )}
        {editorOpen && (
          <div className="depot-live-alert-editor">
            <div className="depot-live-alert-editor-heading">
              <h4>{readOnly ? "Subscription Settings" : editor.id ? "Edit Subscription" : "New Subscription"}</h4>
              <button type="button" onClick={() => this.setState({ editorOpen: false })}>×</button>
            </div>
            <div className="depot-live-alert-fields depot-live-alert-name-field">
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control value={editor.name} disabled={readOnly} onChange={(e) => this.setEditor({ name: e.target.value })} />
              </Form.Group>
            </div>

            <div className="depot-live-alert-toggle-row">
              <Form.Check type="checkbox" checked={editor.notifyOnEntry} disabled={readOnly} label="Entry alerts" onChange={(e) => this.setEditor({ notifyOnEntry: e.target.checked })} />
              <Form.Check type="checkbox" checked={editor.notifyOnExit} disabled={readOnly} label="Exit alerts" onChange={(e) => this.setEditor({ notifyOnExit: e.target.checked })} />
              <Form.Check type="checkbox" checked={editor.emailEnabled} disabled={readOnly} label="Enable email delivery" onChange={(e) => this.setEditor({ emailEnabled: e.target.checked })} />
            </div>
            <div className="depot-live-alert-days">
              <div>
                <strong>Active days</strong>
                {!readOnly && <button type="button" onClick={() => this.setEditor({ activeDaysMask: 127 })}>All days</button>}
                {!readOnly && <button type="button" onClick={() => this.setEditor({ activeDaysMask: 62 })}>Weekdays</button>}
                {!readOnly && <button type="button" onClick={() => this.setEditor({ activeDaysMask: 65 })}>Weekends</button>}
              </div>
              <div>
                {DAYS.map((day) => (
                  <button
                    type="button"
                    key={day.bit}
                    className={(editor.activeDaysMask & day.bit) !== 0 ? "selected" : ""}
                    disabled={readOnly}
                    onClick={() => this.toggleDay(day.bit)}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="depot-live-alert-time">
              <Form.Check type="checkbox" checked={editor.allDay} disabled={readOnly} label="All day" onChange={(e) => this.setEditor({ allDay: e.target.checked })} />
              {!editor.allDay && (
                <>
                  <Form.Group>
                    <Form.Label>Start</Form.Label>
                    <Form.Control type="time" value={editor.startTime || ""} disabled={readOnly} onChange={(e) => this.setEditor({ startTime: e.target.value })} />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>End</Form.Label>
                    <Form.Control type="time" value={editor.endTime || ""} disabled={readOnly} onChange={(e) => this.setEditor({ endTime: e.target.value })} />
                  </Form.Group>
                </>
              )}
              <span>Timezone: {options.timeZoneId || editor.timeZoneId || "Customer timezone"}</span>
            </div>

            <div className="depot-live-alert-pickers">
              {this.renderChecklist("Qualifying depot assets", "assetIds", options.assets || [], "No active assets are assigned to this depot.", "Search assets...")}
              {this.renderChecklist("Notification recipients", "recipientUserIds", options.users || [], "No recipients available.", "Search users or email...")}
            </div>
            {!!suspendedAssets.length && (
              <button type="button" className="depot-live-alert-editor-issues" onClick={() => this.openIssues(editor)}>
                <i className="fa fa-exclamation-triangle" />
                Review {suspendedAssets.length} suspended asset issue{suspendedAssets.length === 1 ? "" : "s"}
              </button>
            )}
            {editor.emailEnabled && (
              <div className="depot-live-alert-external-emails">
                <div>
                  <strong>Additional email recipients</strong>
                  <span>For staff who do not have an AssetTrak account.</span>
                </div>
                {!readOnly && <div className="depot-live-alert-external-email-entry">
                  <Form.Control
                    type="email"
                    value={this.state.externalEmailInput}
                    placeholder="name@example.com"
                    onChange={(event) => this.setState({ externalEmailInput: event.target.value, error: "" })}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        this.addExternalEmail();
                      }
                    }}
                  />
                  <Button type="button" variant="outline-primary" onClick={this.addExternalEmail}>Add email</Button>
                </div>}
                {!!editor.externalEmailAddresses.length && (
                  <div className="depot-live-alert-external-email-tags">
                    {editor.externalEmailAddresses.map((email) => (
                      <span key={email}>
                        {email}
                        {!readOnly && <button type="button" onClick={() => this.removeExternalEmail(email)} aria-label={`Remove ${email}`}>×</button>}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="depot-live-alert-editor-actions">
              <Button type="button" variant="outline-secondary" onClick={() => this.setState({ editorOpen: false })}>{readOnly ? "Close" : "Cancel"}</Button>
              {!readOnly && <Button type="button" variant="primary" disabled={saving} onClick={this.save}>
                {saving ? "Saving..." : "Save Subscription"}
              </Button>}
            </div>
          </div>
        )}
        {message && <p className="depot-live-alert-message">{message}</p>}
        {error && <p className="depot-live-alert-error">{error}</p>}
        <Modal
          show={!!issueSubscription}
          onHide={() => !this.state.resolvingIssue && this.setState({ issueSubscriptionId: "" })}
          centered
          size="lg"
          dialogClassName="depot-live-alert-issue-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Review depot notification changes</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="depot-live-alert-issue-intro">
              Confirm the current allocation and status to remove every depot-alert association for which this asset is now ineligible, or remedy an accidental change to restore this subscription.
            </p>
            <div className="depot-live-alert-issue-list">
              {(issueSubscription?.suspendedAssets || []).map((asset) => (
                <div className="depot-live-alert-issue-card" key={asset.id}>
                  <div>
                    <strong>{asset.name}</strong>
                    <span>{asset.reason}</span>
                  </div>
                  {asset.statusIneligible && (
                    <Form.Control
                      as="select"
                      value={this.state.issueStatusSelections[String(asset.id)] || ""}
                      onChange={(event) => this.setState((state) => ({
                        issueStatusSelections: {
                          ...state.issueStatusSelections,
                          [String(asset.id)]: Number(event.target.value),
                        },
                      }))}
                    >
                      {(options.eligibleStatuses || []).map((status) => (
                        <option key={status.id} value={status.id}>Restore status to {status.name}</option>
                      ))}
                    </Form.Control>
                  )}
                  <div className="depot-live-alert-issue-actions">
                    <Button
                      type="button"
                      variant="outline-danger"
                      disabled={this.state.resolvingIssue}
                      onClick={() => this.resolveIssue(asset, "Confirm")}
                    >
                      Confirm current change
                    </Button>
                    {!String(asset.reason).toLowerCase().includes("no longer available") && (
                      <Button
                        type="button"
                        variant="primary"
                        disabled={this.state.resolvingIssue}
                        onClick={() => this.resolveIssue(asset, "Remedy")}
                      >
                        {asset.depotMismatch && asset.statusIneligible
                          ? "Restore depot and status"
                          : asset.depotMismatch
                            ? "Restore depot allocation"
                            : "Restore eligible status"}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Modal.Body>
        </Modal>
      </section>
    );
  }
}

export default DepotLiveAlertSubscriptions;
